import React, { cloneElement, isValidElement, memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Popover } from 'antd';
import { X } from 'lucide-react';
import styled from 'styled-components';
import { TooltipPlacement } from 'antd/lib/tooltip';

import { useTeacherMiniCard } from 'core/hooks';
import { useIsMobile, useIsTouch } from 'core/hooks/Layout';
import { MobileIconButton, TouchCardFrame } from 'molecules';

import { TeacherMiniCard } from './TeacherMiniCard';

interface IProps {
  teacherId: string;
  placement?: TooltipPlacement;
  trigger?: 'hover' | 'click';
  children: React.ReactNode;
}

/**
 * ТЗ 0122 — мини-карточка преподавателя у любого якоря, как
 * `StudentHoverCard` (ТЗ 0009): задержка 0,4 с, запрос — только когда
 * карточка открылась, повторное открытие — из кеша Apollo.
 *
 * ТЗ 0121 (как у `StudentHoverCard`): на касании — по нажатию и с
 * крестиком; на телефоне с касанием — окно на весь экран (`mobile.css`).
 */
export const TeacherHoverCard = memo<IProps>(
  ({ teacherId, placement = 'rightTop', trigger = 'hover', children }) => {
    const { t } = useTranslation();
    const [armed, setArmed] = useState(false);
    const [visible, setOpen] = useState(false);
    const { card, loading, failed, refetch } = useTeacherMiniCard(teacherId, {
      skip: !armed,
    });
    const isTouch = useIsTouch();
    const isMobile = useIsMobile();
    const asModal = isMobile && isTouch;
    const miniCard = (
      <TeacherMiniCard
        teacherId={teacherId}
        card={card}
        loading={loading || !armed}
        failed={failed}
        onRetry={refetch}
      />
    );

    // Окно и поповер — порталы, но события React всплывают по дереву
    // компонентов: нажатие внутри карточки не должно дойти до якоря.
    const stop = (event: React.SyntheticEvent) => event.stopPropagation();

    if (asModal) {
      const open = () => {
        setArmed(true);
        setOpen(true);
      };
      const anchor = isValidElement(children)
        ? cloneElement(children as React.ReactElement, {
            onClick: (event: React.MouseEvent) => {
              const own = (children as React.ReactElement).props.onClick;
              if (typeof own === 'function') own(event);
              open();
            },
            'aria-haspopup': 'dialog',
            'aria-expanded': visible,
          })
        : children;
      return (
        <>
          {anchor}
          {/* Пустой span — только граница всплытия для портала окна. */}
          <span onClick={stop} onKeyDown={stop} role="presentation">
            <Modal
              visible={visible}
              closable={false}
              title={
                <ModalTitle>
                  <span>{t('mobile.communicator.teacherCardTitle')}</span>
                  <MobileIconButton
                    icon={X}
                    aria-label={t('button.close')}
                    onClick={() => setOpen(false)}
                  />
                </ModalTitle>
              }
              footer={null}
              destroyOnClose
              onCancel={() => setOpen(false)}
            >
              {miniCard}
            </Modal>
          </span>
        </>
      );
    }

    return (
      <Popover
        content={
          isTouch ? (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <div onClick={stop}>
              <TouchCardFrame onClose={() => setOpen(false)}>
                {miniCard}
              </TouchCardFrame>
            </div>
          ) : (
            miniCard
          )
        }
        overlayClassName={isTouch ? 'erp-touch-card' : undefined}
        placement={placement}
        trigger={isTouch ? 'click' : trigger}
        visible={visible}
        mouseEnterDelay={0.4}
        mouseLeaveDelay={0.1}
        destroyTooltipOnHide
        onVisibleChange={(next) => {
          if (next) setArmed(true);
          setOpen(next);
        }}
      >
        {children}
      </Popover>
    );
  },
);

TeacherHoverCard.displayName = 'TeacherHoverCard';

const ModalTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: -8px -8px -8px 0;
`;
